import { useEffect, type RefObject } from 'react';
import { ESCAPE_PRIORITY, useEscapeAction } from '@peruselab/structure-editor/components';

// #525 -- AccountMenu, MoreMenu, and FeedbackDisclosure each wired up this exact pair of
// capture-phase document listeners by hand: Escape closes and returns focus to the trigger; an
// outside pointerdown closes without moving focus (it already went wherever the user clicked).
// One shared implementation, so a future fix to either half (pointercancel handling, focus-return
// edge cases, etc.) only has to land once.
export function useDismissableOverlay(
  open: boolean,
  setOpen: (open: boolean) => void,
  triggerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>
): void {
  // #762 -- Escape through the shared arbiter (overlay priority).
  useEscapeAction(
    ESCAPE_PRIORITY.overlay,
    () => {
      setOpen(false);
      triggerRef.current?.focus();
      return true;
    },
    open
  );
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!contentRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, setOpen, triggerRef, contentRef]);
}
